import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  marcas as api,
  type CaixaDoChatwoot,
  type CaixaNaLista,
  type PedidoDeCaixa,
} from '../api/assistentes'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { mensagemDeErro } from './mensagemDeErro'
import { marcaDaRota } from './marcaDaRota'
import { useSessao } from '../auth/useSessao'
import './times.css'
import './usuarios.css'

/**
 * Os canais do Chatwoot ligados a uma marca.
 *
 * 🔴 **O `idExterno` é como o webhook descobre de quem é a mensagem.** Em 07/08
 * o banco tinha os números de exemplo do roteiro e o Chatwoot real tinha outro:
 * todo evento caía em `Descartado`, em silêncio e por design, e a correção — um
 * número — exigiu `update` direto no banco de produção.
 *
 * Por isso o campo diz **onde achar o número**, e há como conferir a lista real
 * sem digitar: "id externo" não significa nada para quem opera.
 *
 * 🔴 **A tela só editava.** Até 21/08 não havia como *criar* um canal aqui — o
 * `POST` existia na API e nenhuma tela o chamava. Quem precisava ligar um canal
 * novo fazia a única coisa possível: editava um existente. Foi o que aconteceu
 * ao ligar o WhatsApp — o Telegram da Di Giorgio virou o WhatsApp e parou de
 * atender, sem erro nenhum, porque o `IdExterno` dele deixou de existir.
 *
 * O `TipoDeIdentificador` tinha o mesmo problema pelo avesso: não havia campo,
 * então o valor só era reenviado como estava. Um canal editado herdava o tipo
 * do canal anterior — WhatsApp ficou marcado como Telegram.
 */

/**
 * Os tipos que o back aceita (`TipoIdentificador`), como string do enum.
 *
 * 🔴 **`Site` faltava aqui** (demanda 291). O back o conhece desde a 081, e as
 * caixas de site eram gravadas direto no banco. Ao editar uma delas, o seletor
 * não tinha a opção do valor gravado e mostrava a primeira da lista — "Telefone"
 * para um canal em que o visitante é anônimo.
 */
const TIPOS = ['Telefone', 'Bsuid', 'Instagram', 'Telegram', 'Email', 'Site'] as const

const VAZIO: PedidoDeCaixa = {
  idExterno: 0,
  nome: '',
  // WhatsApp é o caso mais comum e o que motivou este formulário.
  tipoDeIdentificador: 'Telefone',
  canalExterno: '',
  ativa: true,
}

export function Caixas() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const marca = marcaDaRota()

  const [lista, setLista] = useState<CaixaNaLista[] | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [rascunho, setRascunho] = useState<Record<number, PedidoDeCaixa>>({})
  const [salvando, setSalvando] = useState<number | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [recado, setRecado] = useState<string | null>(null)

  const [doChatwoot, setDoChatwoot] = useState<CaixaDoChatwoot[] | null>(null)
  const [erroDoChatwoot, setErroDoChatwoot] = useState<string | null>(null)
  const [conferindo, setConferindo] = useState(false)

  const [novo, setNovo] = useState<PedidoDeCaixa>(VAZIO)
  const [criando, setCriando] = useState(false)

  const gerencia = pode('ParametroGerenciar')

  const carregar = useCallback(async () => {
    if (!marca) return

    setFalhou(false)
    try {
      setLista(await api.caixas(marca))
    } catch {
      setFalhou(true)
    }
  }, [marca])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (!marca) return null

  function valorDe(c: CaixaNaLista): PedidoDeCaixa {
    return (
      rascunho[c.id] ?? {
        idExterno: c.idExterno,
        nome: c.nome,
        tipoDeIdentificador: c.tipoDeIdentificador,
        canalExterno: c.canalExterno,
        ativa: c.ativa,
      }
    )
  }

  function editar(c: CaixaNaLista, mudanca: Partial<PedidoDeCaixa>) {
    setRascunho({ ...rascunho, [c.id]: { ...valorDe(c), ...mudanca } })
  }

  async function salvar(c: CaixaNaLista) {
    setSalvando(c.id)
    setErro(null)
    try {
      await api.alterarCaixa(marca!, c.id, valorDe(c))

      const limpo = { ...rascunho }
      delete limpo[c.id]
      setRascunho(limpo)

      setRecado(t('caixas.salvo'))
      await carregar()
    } catch (e) {
      // O 409 do back nomeia a outra caixa. Trocá-lo por uma frase genérica
      // obrigaria a abrir o banco para descobrir qual é.
      setErro(mensagemDeErro(e, t))
    } finally {
      setSalvando(null)
    }
  }

  async function criar() {
    setCriando(true)
    setErro(null)
    try {
      await api.criarCaixa(
        marca!,
        novo.idExterno,
        novo.nome,
        novo.tipoDeIdentificador,
        novo.canalExterno ?? '',
      )

      setNovo(VAZIO)
      setRecado(t('caixas.criado'))
      await carregar()
    } catch (e) {
      // O 409 do back nomeia o canal que já ocupa esse número e em que marca
      // ele está — é a informação que evita o `IdExterno` duplicado.
      setErro(mensagemDeErro(e, t))
    } finally {
      setCriando(false)
    }
  }

  async function conferir() {
    setConferindo(true)
    setErroDoChatwoot(null)
    try {
      const r = await api.caixasNoChatwoot(marca!)
      setDoChatwoot(r.caixas)
      setErroDoChatwoot(r.erro)
    } catch {
      setErroDoChatwoot(t('caixas.semResposta'))
    } finally {
      setConferindo(false)
    }
  }

  return (
    <>
      <header className="times__topo">
        <div>
          <h1 className="times__titulo">{t('caixas.titulo')}</h1>
          <p className="times__descricao">{t('caixas.descricao')}</p>
        </div>

        {gerencia && (
          <Botao variante="discreto" carregando={conferindo} onClick={() => void conferir()}>
            {t('caixas.conferir')}
          </Botao>
        )}
      </header>

      <Aviso tom="alerta">{t('caixas.aviso')}</Aviso>

      {recado && <Aviso tom="sucesso">{recado}</Aviso>}
      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {erroDoChatwoot && <Aviso tom="alerta">{erroDoChatwoot}</Aviso>}

      {doChatwoot && doChatwoot.length > 0 && (
        <Cartao>
          <h2 className="times__nome">{t('caixas.doChatwoot')}</h2>

          <ul className="times__lista">
            {doChatwoot.map((c) => (
              <li key={c.id} className="times__cabecalho">
                <span>
                  <strong>{c.id}</strong> · {c.nome}
                  {c.canal ? ` · ${c.canal}` : ''}
                  {c.jaCadastrada ? ` · ${t('caixas.jaCadastrada')}` : ''}
                </span>

                {/* Preenche o formulário em vez de mandar digitar: o número
                    errado é o defeito mais caro deste cadastro, e digitá-lo é
                    a única forma de errá-lo. */}
                {gerencia && !c.jaCadastrada && (
                  <Botao
                    variante="discreto"
                    onClick={() =>
                      setNovo({
                        ...novo,
                        idExterno: c.id,
                        nome: c.nome,
                        canalExterno: c.canal ?? '',
                      })
                    }
                  >
                    {t('caixas.usarEste')}
                  </Botao>
                )}
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {/* Criar vem ANTES da lista de propósito. Sem este bloco, a única ação
          possível na tela era editar um canal existente — e foi assim que um
          canal em produção virou outro, levando o antigo ao silêncio. */}
      {gerencia && (
        <Cartao titulo={t('caixas.novo')} rotulo={t('caixas.novo')}>
          <p className="times__descricao">{t('caixas.novoAjuda')}</p>

          <div className="times__edicao">
            <Campo
              type="number"
              rotulo={t('caixas.idExterno')}
              value={novo.idExterno ? String(novo.idExterno) : ''}
              onChange={(e) => setNovo({ ...novo, idExterno: Number(e.target.value) })}
              ajuda={t('caixas.idExternoAjuda')}
              obrigatorio
            />

            <Campo
              rotulo={t('caixas.nome')}
              value={novo.nome}
              onChange={(e) => setNovo({ ...novo, nome: e.target.value })}
              obrigatorio
            />

            <label className="usuarios__filtro">
              <span>{t('caixas.tipo')}</span>
              <select
                value={novo.tipoDeIdentificador}
                onChange={(e) => setNovo({ ...novo, tipoDeIdentificador: e.target.value })}
              >
                {TIPOS.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {t(`caixas.tipo${tipo}`)}
                  </option>
                ))}
              </select>
            </label>

            <p className="times__descricao">{t('caixas.tipoAjuda')}</p>

            <Campo
              rotulo={t('caixas.canalExterno')}
              value={novo.canalExterno ?? ''}
              onChange={(e) => setNovo({ ...novo, canalExterno: e.target.value })}
            />

            <div className="times__acoes">
              <Botao
                variante="primario"
                carregando={criando}
                disabled={!novo.idExterno || !novo.nome.trim()}
                onClick={() => void criar()}
              >
                {t('caixas.criar')}
              </Botao>
            </div>
          </div>
        </Cartao>
      )}

      {falhou ? (
        <Estado
          tipo="erro"
          titulo={t('caixas.erroAoCarregar')}
          acao={<Botao onClick={() => void carregar()}>{t('caixas.tentarDeNovo')}</Botao>}
        />
      ) : lista === null ? (
        <Estado tipo="carregando" titulo={t('caixas.carregando')} />
      ) : lista.length === 0 ? (
        <Estado tipo="vazio" titulo={t('caixas.vazio')} />
      ) : (
        <ul className="times__lista">
          {lista.map((c) => (
            <li key={c.id}>
              <Cartao rotulo={c.nome}>
                <div className="times__cabecalho">
                  <h2 className="times__nome">
                    {c.nome}
                    <span className="times__ausente">
                      {c.assistente
                        ? t('caixas.assistente', { nome: c.assistente })
                        : t('caixas.semAssistente')}
                    </span>
                  </h2>
                </div>

                {gerencia && (
                  <div className="times__edicao">
                    <Campo
                      type="number"
                      rotulo={t('caixas.idExterno')}
                      value={String(valorDe(c).idExterno)}
                      onChange={(e) => editar(c, { idExterno: Number(e.target.value) })}
                      ajuda={t('caixas.idExternoAjuda')}
                    />

                    <Campo
                      rotulo={t('caixas.nome')}
                      value={valorDe(c).nome}
                      onChange={(e) => editar(c, { nome: e.target.value })}
                    />

                    {/* Sem este campo o tipo só era reenviado como estava, e um
                        canal editado herdava o tipo do anterior — o WhatsApp
                        ficou marcado como Telegram. */}
                    <label className="usuarios__filtro">
                      <span>{t('caixas.tipo')}</span>
                      <select
                        value={valorDe(c).tipoDeIdentificador}
                        onChange={(e) => editar(c, { tipoDeIdentificador: e.target.value })}
                      >
                        {TIPOS.map((tipo) => (
                          <option key={tipo} value={tipo}>
                            {t(`caixas.tipo${tipo}`)}
                          </option>
                        ))}
                      </select>
                    </label>

                    <p className="times__descricao">{t('caixas.tipoAjuda')}</p>

                    <Campo
                      rotulo={t('caixas.canalExterno')}
                      value={valorDe(c).canalExterno ?? ''}
                      onChange={(e) => editar(c, { canalExterno: e.target.value })}
                    />

                    <label className="usuarios__opcao">
                      <input
                        type="checkbox"
                        checked={valorDe(c).ativa}
                        onChange={(e) => editar(c, { ativa: e.target.checked })}
                      />
                      {t('caixas.ativa')}
                    </label>

                    <p className="times__descricao">{t('caixas.ativaAjuda')}</p>

                    <div className="times__acoes">
                      <Botao
                        variante="primario"
                        carregando={salvando === c.id}
                        onClick={() => void salvar(c)}
                      >
                        {t('caixas.salvar')}
                      </Botao>
                    </div>
                  </div>
                )}
              </Cartao>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
